import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

/** Match slider limits in HeadPlacementTweaks.jsx */
const SHIFT_X_MIN = -10;
const SHIFT_X_MAX = 10;
const SHIFT_Y_MIN = -8;
const SHIFT_Y_MAX = 8;
const SIZE_MIN = 75;
const SIZE_MAX = 130;

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

function touchDistance(touches) {
  if (touches.length < 2) return 0;
  const a = touches[0];
  const b = touches[1];
  return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
}

/**
 * Phone / tablet: narrow layout or coarse pointer → enable drag + pinch on the head on the card.
 */
function useTouchPlacementEnabled() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const query = '(max-width: 991.98px), (pointer: coarse)';
    const mq = window.matchMedia(query);
    const sync = () => setEnabled(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return enabled;
}

/**
 * Card preview: template image defines aspect ratio; overlays use percentages of the card box.
 */
const CardPreview = forwardRef(function CardPreview(
  {
    selectedJersey,
    uploadedHeadImage,
    playerNumber,
    playerName,
    playerPosition,
    headShiftX = 0,
    headShiftY = 0,
    headSizePct = 100,
    onHeadShiftX,
    onHeadShiftY,
    onHeadSizePct,
  },
  ref
) {
  const templateSrc = `/templates/${selectedJersey}.png`;
  const baseWidthPct = (20 * headSizePct) / 100;
  const cardInnerRef = useRef(null);
  const headSlotRef = useRef(null);
  const touchPlacement = useTouchPlacementEnabled();

  const placementRef = useRef({
    headShiftX,
    headShiftY,
    headSizePct,
    onHeadShiftX,
    onHeadShiftY,
    onHeadSizePct,
  });

  useEffect(() => {
    placementRef.current = {
      headShiftX,
      headShiftY,
      headSizePct,
      onHeadShiftX,
      onHeadShiftY,
      onHeadSizePct,
    };
  }, [headShiftX, headShiftY, headSizePct, onHeadShiftX, onHeadShiftY, onHeadSizePct]);

  const gestureRef = useRef({
    mode: 'none',
    startX: 0,
    startY: 0,
    startShiftX: 0,
    startShiftY: 0,
    startDist: 0,
    startSize: 100,
  });

  const onTouchStart = useCallback(
    (e) => {
      if (!touchPlacement || !uploadedHeadImage) return;
      const inner = cardInnerRef.current;
      if (!inner) return;

      const t = e.touches;
      if (t.length === 2) {
        const d = touchDistance(t);
        if (d < 8) return;
        e.preventDefault();
        const p = placementRef.current;
        gestureRef.current = {
          mode: 'pinch',
          startX: 0,
          startY: 0,
          startShiftX: 0,
          startShiftY: 0,
          startDist: d,
          startSize: p.headSizePct,
        };
      } else if (t.length === 1) {
        gestureRef.current = {
          mode: 'pan',
          startX: t[0].clientX,
          startY: t[0].clientY,
          startShiftX: placementRef.current.headShiftX,
          startShiftY: placementRef.current.headShiftY,
          startDist: 0,
          startSize: placementRef.current.headSizePct,
        };
      }
    },
    [touchPlacement, uploadedHeadImage]
  );

  const onTouchMove = useCallback(
    (e) => {
      const g = gestureRef.current;
      if (g.mode === 'none') return;
      const inner = cardInnerRef.current;
      if (!inner) return;
      const rect = inner.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;

      if (g.mode === 'pinch' && e.touches.length >= 2) {
        e.preventDefault();
        const d = touchDistance(e.touches);
        if (g.startDist < 8) return;
        const ratio = d / g.startDist;
        const next = clamp(Math.round(g.startSize * ratio * 10) / 10, SIZE_MIN, SIZE_MAX);
        placementRef.current.onHeadSizePct?.(next);
      } else if (g.mode === 'pan' && e.touches.length === 1) {
        e.preventDefault();
        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        const dx = x - g.startX;
        const dy = y - g.startY;
        const dShiftX = (dx / rect.width) * 100;
        const dShiftY = (dy / rect.height) * 100;
        const nx = Math.round(clamp(g.startShiftX + dShiftX, SHIFT_X_MIN, SHIFT_X_MAX) * 2) / 2;
        const ny = Math.round(clamp(g.startShiftY + dShiftY, SHIFT_Y_MIN, SHIFT_Y_MAX) * 2) / 2;
        placementRef.current.onHeadShiftX?.(nx);
        placementRef.current.onHeadShiftY?.(ny);
      }
    },
    []
  );

  const onTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      gestureRef.current = { mode: 'none', startX: 0, startY: 0, startShiftX: 0, startShiftY: 0, startDist: 0, startSize: 100 };
    } else if (gestureRef.current.mode === 'pinch' && e.touches.length === 1) {
      gestureRef.current = { mode: 'none', startX: 0, startY: 0, startShiftX: 0, startShiftY: 0, startDist: 0, startSize: 100 };
    }
  }, []);

  useEffect(() => {
    const slot = headSlotRef.current;
    if (!slot || !uploadedHeadImage || !touchPlacement) return;

    const opts = { passive: false };
    const move = (ev) => onTouchMove(ev);
    const end = (ev) => onTouchEnd(ev);

    slot.addEventListener('touchstart', onTouchStart, opts);
    slot.addEventListener('touchmove', move, opts);
    slot.addEventListener('touchend', end);
    slot.addEventListener('touchcancel', end);

    return () => {
      slot.removeEventListener('touchstart', onTouchStart, opts);
      slot.removeEventListener('touchmove', move, opts);
      slot.removeEventListener('touchend', end);
      slot.removeEventListener('touchcancel', end);
    };
  }, [uploadedHeadImage, touchPlacement, onTouchStart, onTouchMove, onTouchEnd]);

  const touchable = !!(uploadedHeadImage && touchPlacement);

  return (
    <div ref={ref} className="pcg-card-wrap">
      <div ref={cardInnerRef} className="pcg-card-inner">
        <img
          key={selectedJersey}
          src={templateSrc}
          alt=""
          className="pcg-template-img"
          draggable={false}
        />

        {uploadedHeadImage ? (
          <div
            ref={headSlotRef}
            className={`pcg-head-slot${touchable ? ' pcg-head-slot--touchable' : ''}`}
            style={{
              left: `${50 + headShiftX}%`,
              top: `calc(9.5% + ${headShiftY}%)`,
              width: `${baseWidthPct}%`,
            }}
          >
            <img src={uploadedHeadImage} alt="" draggable={false} />
          </div>
        ) : null}

        {String(playerNumber ?? '').trim() ? (
          <div className="pcg-overlay-num-card">{playerNumber}</div>
        ) : null}

        <div className="pcg-overlay-name">{playerName}</div>

        <div className="pcg-overlay-pos">{playerPosition}</div>
      </div>
    </div>
  );
});

CardPreview.displayName = 'CardPreview';

export default CardPreview;
